import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { DownloadSimple, FunnelSimple, PencilSimple, ArrowCounterClockwise, X, Warning, CaretDown, CaretRight, Wallet } from "@phosphor-icons/react";
import { fmtMoney } from "@/lib/format";


// ============================================================
// Salary Advance Tracker — installment ledger view surfaced on
// the Payroll page. Powered by GET /api/payroll/advance/tracker.
// ============================================================
function AdvanceTrackerPanel({ month, branchId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (branchId) params.branch_id = branchId;
      const { data: res } = await api.get("/payroll/advance/tracker", { params });
      setData(res);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [month, branchId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const totals = data?.totals || {};
  return (
    <div className="border border-canvas-line bg-canvas-card" data-testid="advance-tracker-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="advance-tracker-toggle"
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-canvas-panel/60"
      >
        <div className="flex items-center gap-2">
          {open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
          <Wallet size={14} weight="bold" />
          <span className="font-display text-sm font-semibold">Salary Advance Tracker</span>
          <span className="font-mono text-[10px] text-ink-subtle">— {month}</span>
        </div>
        {data && (
          <div className="flex items-center gap-4 font-mono text-[10px]">
            <span><span className="text-ink-subtle uppercase tracking-widest">Employees</span> <span className="tabular-nums font-bold">{totals.employee_count || 0}</span></span>
            <span><span className="text-ink-subtle uppercase tracking-widest">This Month</span> <span className="tabular-nums font-bold">₹{fmtMoney(totals.deduction_this_month || 0)}</span></span>
            <span><span className="text-ink-subtle uppercase tracking-widest">Outstanding</span> <span className="tabular-nums font-bold">₹{fmtMoney(totals.outstanding_after || 0)}</span></span>
          </div>
        )}
      </button>
      {open && (
        <div className="border-t border-canvas-line overflow-x-auto">
          {loading ? (
            <div className="p-6 text-center font-mono text-xs text-ink-subtle">Loading…</div>
          ) : (data?.rows || []).length === 0 ? (
            <div className="p-6 text-center font-mono text-xs text-ink-subtle">No employees have an active salary advance in {month}.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-canvas-panel/50 border-b border-canvas-line">
                <tr>
                  {[
                    { l: "Branch" }, { l: "Code" }, { l: "Employee" },
                    { l: "Total ₹", r: true }, { l: "Installments", r: true }, { l: "Start" },
                    { l: "EMI ₹", r: true }, { l: "Paid so far ₹", r: true }, { l: "This month ₹", r: true },
                    { l: "Balance ₹", r: true }, { l: "Months left", r: true },
                  ].map((h, i) => (
                    <th
                      key={`${h.l}-${i}`}
                      className={`px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted ${h.r ? "text-right" : "text-left"}`}
                    >
                      {h.l}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.employee_id} data-testid={`advance-row-${r.employee_id}`} className="border-b border-canvas-line last:border-b-0">
                    <td className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider">{r.branch_name || "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{r.employee_code || "—"}</td>
                    <td className="px-2 py-1.5">{r.name}{r.legacy ? <span className="ml-2 px-1 py-0.5 border border-canvas-line text-[9px] font-mono uppercase text-ink-muted">Legacy</span> : null}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.legacy ? "—" : fmtMoney(r.total)}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.legacy ? "—" : r.installments}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{r.start_month || "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums">{fmtMoney(r.monthly_emi)}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.legacy ? "—" : fmtMoney(r.paid_so_far)}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-destructive">{fmtMoney(r.deduction_now)}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums font-semibold">{r.legacy ? "—" : fmtMoney(r.balance_after)}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.legacy ? "—" : r.months_remaining}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Same column set (order & names) as your existing AMARAVATI PAYROLL sheet
// so CSV drops in with zero remap work.
const CSV_HEADERS = [
  "DATE", "MONTH", "NUMBER_DAYS", "PAY_MONTH", "NAME", "EMP_CODE", "BRANCH",
  "DEISGANTION", "WORK_AREA", "ESI_PF", "ESI_PF_BRANCH", "MODE_OF_PAY",
  "BANK_ACCOUNT", "IFSC_CODE", "BANK_NAME", "GROSS_SALALRY", "SALARY_PER_DAY",
  "NO OF WORKING HOURS", "NO OF WORKING DAYS", "HOURS", "DAYS", "OT HOURS",
  "OT DAYS", "NO_OF_HOLIDAYS", "OT_PAY", "HOL_DAY_PAY_MINUS", "H_PAY_BONUS_Y_N",
  "HOLIDAY_PAY_BONUS", "GROSS_PAY_BEFORE_ESI_PF", "PF", "ESI", "SAL_ADV",
  "NET_PAY", "NET_PAY_ROUND", "STATUS", "SBI_Y_N", "PAY_DAY_OF_MONTH",
];

function fmtINR(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function fmt2(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function PayrollPreviewPage() {
  const { user } = useAuth();
  const isAdminHR = user?.role === "admin" || (user?.permissions || []).includes("branches.view_all");

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState({ rows: [], totals: {}, number_days: 30 });
  const [loading, setLoading] = useState(false);
  // Override modal + inline edit state.
  const [modalRow, setModalRow] = useState(null);
  // Track pending inline edits: key = employee_id → { value, saving }
  const [pending, setPending] = useState({});

  const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month: monthStr };
      if (isAdminHR && branchId) params.branch_id = branchId;
      const { data: d } = await api.get("/payroll/preview", { params });
      setData(d);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [monthStr, branchId, isAdminHR]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isAdminHR) return;
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
  }, [isAdminHR]);

  const saveOverride = async (employee_id, value, note = "") => {
    const parsed = Math.max(0, parseInt(value, 10) || 0);
    try {
      await api.put("/payroll/overrides", { employee_id, month: monthStr, no_of_holidays: parsed, note });
      toast.success("Override saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetOverride = async (employee_id) => {
    if (!window.confirm("Reset override and revert to auto-computed LOP days?")) return;
    try {
      await api.delete(`/payroll/overrides/${employee_id}/${monthStr}`);
      toast.success("Override cleared");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const commitInline = async (row) => {
    const p = pending[row.employee_id];
    if (!p || p.value === "" || p.value === undefined) return;
    const val = parseInt(p.value, 10);
    if (Number.isNaN(val) || val < 0) {
      toast.error("Enter a whole number ≥ 0");
      return;
    }
    if (val > (data.number_days || 31)) {
      toast.error(`Cannot exceed calendar days in month (${data.number_days})`);
      return;
    }
    setPending((prev) => ({ ...prev, [row.employee_id]: { ...p, saving: true } }));
    await saveOverride(row.employee_id, val);
    setPending((prev) => {
      const n = { ...prev };
      delete n[row.employee_id];
      return n;
    });
  };

  const grouped = useMemo(() => {
    const g = {};
    (data.rows || []).forEach((r) => {
      const key = r.branch_name || "(no branch)";
      g[key] = g[key] || [];
      g[key].push(r);
    });
    return g;
  }, [data]);

  const csvEscape = (v) => {
    const s = String(v ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  };

  // Excel strips leading zeros from numeric-looking cells when opening a
  // CSV. Prepending an apostrophe forces the cell to be typed as text on
  // paste, preserving zeros in bank account numbers (some banks issue
  // 10-13 digit accounts that legitimately start with 0).
  const asText = (v) => {
    const s = String(v ?? "").trim();
    return s ? `'${s}` : "";
  };

  const exportCsv = () => {
    const rows = data.rows || [];
    if (rows.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    const lines = [CSV_HEADERS.join(",")];
    const lastDay = `${String(data.number_days).padStart(2, "0")}/${String(month + 1).padStart(2, "0")}/${year}`;
    rows.forEach((r) => {
      const cells = [
        lastDay,                                    // DATE
        data.month_str,                             // MONTH e.g. 202606
        data.number_days,                           // NUMBER_DAYS
        data.pay_month_str,                         // PAY_MONTH
        r.name, r.employee_code, r.branch_name,
        r.designation, r.work_area, r.esi_pf, r.esi_pf_branch, r.mode_of_pay,
        asText(r.bank_account), r.ifsc_code, r.bank_name,
        r.gross_salary,
        r.salary_per_day,
        r.working_hours_per_month,
        r.working_days_per_month,
        r.hours_worked,
        r.days_worked,
        r.ot_hours,
        r.ot_days,
        r.no_of_holidays,
        r.ot_pay,
        r.hol_day_pay_minus,
        r.h_pay_bonus_y_n,
        r.holiday_pay_bonus,
        r.gross_before,
        r.pf,
        r.esi,
        r.sal_adv,
        r.net_pay,
        r.net_pay_round,
        r.status,
        r.sbi_y_n,
        r.salary_credit_day ?? "",
      ].map(csvEscape);
      lines.push(cells.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `PAYROLL_${data.month_str}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row(s)`);
  };

  const yearOptions = [];
  for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y += 1) yearOptions.push(y);

  const t = data.totals || {};

  return (
    <div>
      <PageHeader
        eyebrow="HR / 06"
        title="Payroll Preview"
        subtitle="Monthly salary computation with LOP, OT, PF (12%, cap ₹1,800), and ESI (0.75%, cap ₹21k). Read-only preview — CSV export matches your existing AMARAVATI payroll layout."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={exportCsv}
              disabled={loading || (data.rows || []).length === 0}
              data-testid="payroll-export-csv"
              className="px-4 py-2.5 border border-ink text-sm font-semibold hover:bg-canvas-panel disabled:opacity-40 flex items-center gap-2"
            >
              <DownloadSimple size={14} weight="bold" />
              Export CSV
            </button>
          </div>
        }
      />

      <div className="px-8 py-6 space-y-4">
        {/* Filters */}
        <div className="border border-canvas-line bg-canvas-card p-4 flex items-center gap-3 flex-wrap" data-testid="payroll-filters">
          <FunnelSimple size={14} className="text-ink-muted" />
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value, 10))}
            data-testid="payroll-month"
            className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm"
          >
            {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            data-testid="payroll-year"
            className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm"
          >
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {isAdminHR && (
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              data-testid="payroll-branch"
              className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm"
            >
              <option value="">All branches</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <div className="ml-auto font-mono text-[10px] text-ink-muted">
            {loading ? "Loading…" : `${(data.rows || []).length} employee${(data.rows || []).length === 1 ? "" : "s"} · ${data.number_days} days in month`}
          </div>
        </div>

        {/* Summary strip */}
        {(data.rows || []).length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="payroll-totals">
            <div className="border border-canvas-line bg-canvas-card p-3">
              <div className="font-mono text-[9px] uppercase tracking-widest text-ink-muted">Gross Before</div>
              <div className="font-display text-xl font-bold tabular-nums">₹{fmtINR(t.gross_before)}</div>
            </div>
            <div className="border border-canvas-line bg-canvas-card p-3">
              <div className="font-mono text-[9px] uppercase tracking-widest text-emerald-700">OT Pay</div>
              <div className="font-display text-xl font-bold tabular-nums">₹{fmtINR(t.ot_pay)}</div>
            </div>
            <div className="border border-canvas-line bg-canvas-card p-3">
              <div className="font-mono text-[9px] uppercase tracking-widest text-destructive">LOP</div>
              <div className="font-display text-xl font-bold tabular-nums">₹{fmtINR(t.hol_day_pay_minus)}</div>
            </div>
            <div className="border border-canvas-line bg-canvas-card p-3">
              <div className="font-mono text-[9px] uppercase tracking-widest text-ink-muted">PF (12%)</div>
              <div className="font-display text-xl font-bold tabular-nums">₹{fmtINR(t.pf)}</div>
              <div className="font-mono text-[9px] text-ink-muted">Employer: ₹{fmtINR(t.employer_pf)}</div>
            </div>
            <div className="border border-canvas-line bg-canvas-card p-3">
              <div className="font-mono text-[9px] uppercase tracking-widest text-ink-muted">ESI (0.75%)</div>
              <div className="font-display text-xl font-bold tabular-nums">₹{fmtINR(t.esi)}</div>
              <div className="font-mono text-[9px] text-ink-muted">Employer: ₹{fmtINR(t.employer_esi)}</div>
            </div>
            <div className="border-2 border-ink bg-canvas p-3">
              <div className="font-mono text-[9px] uppercase tracking-widest text-ink">Net Payable</div>
              <div className="font-display text-2xl font-bold tabular-nums">₹{fmtINR(t.net_pay_round)}</div>
            </div>
          </div>
        )}

        {loading && <div className="text-center font-mono text-xs text-ink-subtle py-12">Loading payroll…</div>}
        {!loading && (data.rows || []).length === 0 && (
          <div className="border border-canvas-line bg-canvas-card p-12 text-center font-mono text-xs text-ink-subtle">
            No employees on payroll for the selected filters.
          </div>
        )}

        <AdvanceTrackerPanel month={monthStr} branchId={branchId} />
        {!loading && Object.entries(grouped).map(([branchName, branchRows]) => (
          <div key={branchName} className="border border-canvas-line bg-canvas-card overflow-hidden" data-testid={`payroll-branch-${branchName}`}>
            <div className="px-4 py-2.5 bg-canvas-panel border-b border-canvas-line flex items-center justify-between">
              <div className="text-sm font-semibold">{branchName}</div>
              <div className="font-mono text-[10px] text-ink-muted">{branchRows.length} employee{branchRows.length === 1 ? "" : "s"}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-canvas-line bg-canvas-panel/40">
                    <th className="text-left px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted">Code</th>
                    <th className="text-left px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted">Name</th>
                    <th className="text-left px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted">Designation</th>
                    <th className="text-center px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted">ESI/PF</th>
                    <th className="text-right px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted">Gross</th>
                    <th className="text-right px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted">Per Day</th>
                    <th className="text-right px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted">Days</th>
                    <th className="text-center px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted">Holidays</th>
                    <th className="text-right px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-destructive">LOP ₹</th>
                    <th className="text-right px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-emerald-700">OT Pay</th>
                    <th className="text-right px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-amber-600">Bonus</th>
                    <th className="text-right px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted">Gross Bef.</th>
                    <th className="text-right px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted">PF</th>
                    <th className="text-right px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted">ESI</th>
                    <th className="text-right px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted">Adv</th>
                    <th className="text-right px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-ink">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {branchRows.map((r) => (
                    <tr key={r.employee_id} data-testid={`payroll-row-${r.employee_id}`} className="border-b border-canvas-line hover:bg-canvas-panel/30">
                      <td className="px-2 py-1.5 font-mono text-[11px]">{r.employee_code}</td>
                      <td className="px-2 py-1.5">
                        <div className="text-xs font-semibold">{r.name}</div>
                        <div className="font-mono text-[9px] text-ink-muted">{r.work_area || ""}</div>
                      </td>
                      <td className="px-2 py-1.5 text-[11px]">{r.designation || ""}</td>
                      <td className="text-center px-2 py-1.5">
                        <span className={`font-mono text-[9px] font-semibold px-1.5 py-0.5 ${r.esi_pf === "YES" ? "bg-emerald-100 text-emerald-800" : "bg-neutral-100 text-neutral-500"}`}>
                          {r.esi_pf}
                        </span>
                      </td>
                      <td className="text-right px-2 py-1.5 font-mono tabular-nums">{fmt2(r.gross_salary)}</td>
                      <td className="text-right px-2 py-1.5 font-mono tabular-nums text-ink-muted">{fmt2(r.salary_per_day)}</td>
                      <td className="text-right px-2 py-1.5 font-mono tabular-nums">
                        <span title={`Working: ${r.working_days_per_month}`}>
                          {r.days_worked}/{r.working_days_per_month}
                        </span>
                      </td>
                      {/* Editable holidays / LOP-days count with override badge */}
                      <td className="text-center px-2 py-1.5">
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="number"
                            min={0}
                            max={data.number_days}
                            value={pending[r.employee_id]?.value ?? r.no_of_holidays}
                            onChange={(e) => setPending((prev) => ({
                              ...prev,
                              [r.employee_id]: { value: e.target.value, saving: false },
                            }))}
                            onBlur={() => pending[r.employee_id] && commitInline(r)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.currentTarget.blur(); }
                              if (e.key === "Escape") {
                                setPending((prev) => {
                                  const n = { ...prev }; delete n[r.employee_id]; return n;
                                });
                                e.currentTarget.blur();
                              }
                            }}
                            data-testid={`payroll-hol-input-${r.employee_id}`}
                            disabled={pending[r.employee_id]?.saving}
                            className={`w-12 px-1 py-0.5 bg-canvas border text-xs font-mono tabular-nums text-center ${r.override ? "border-amber-500 font-bold" : "border-canvas-line"}`}
                          />
                          <button
                            onClick={() => setModalRow(r)}
                            data-testid={`payroll-hol-edit-${r.employee_id}`}
                            className="p-0.5 text-ink-muted hover:text-ink"
                            title="Adjust with note"
                          >
                            <PencilSimple size={11} weight="bold" />
                          </button>
                          {r.override && (
                            <button
                              onClick={() => resetOverride(r.employee_id)}
                              data-testid={`payroll-hol-reset-${r.employee_id}`}
                              className="p-0.5 text-amber-600 hover:text-destructive"
                              title={`Override: ${r.override.no_of_holidays} (was ${r.computed_lop_days}). Click to reset.`}
                            >
                              <ArrowCounterClockwise size={11} weight="bold" />
                            </button>
                          )}
                        </div>
                        {r.override && (
                          <div className="text-[9px] font-mono text-amber-600 mt-0.5" data-testid={`payroll-hol-ovr-badge-${r.employee_id}`}>
                            OVERRIDE
                          </div>
                        )}
                      </td>
                      <td className="text-right px-2 py-1.5 font-mono tabular-nums text-destructive">
                        {r.hol_day_pay_minus < 0 ? fmt2(r.hol_day_pay_minus) : ""}
                      </td>
                      <td className="text-right px-2 py-1.5 font-mono tabular-nums text-emerald-700">
                        {r.ot_pay > 0 ? fmt2(r.ot_pay) : ""}
                      </td>
                      <td className="text-right px-2 py-1.5 font-mono tabular-nums text-amber-600">
                        {r.holiday_pay_bonus > 0 ? fmt2(r.holiday_pay_bonus) : ""}
                      </td>
                      <td className="text-right px-2 py-1.5 font-mono tabular-nums font-semibold">{fmt2(r.gross_before)}</td>
                      <td className="text-right px-2 py-1.5 font-mono tabular-nums">{r.pf > 0 ? r.pf : ""}</td>
                      <td className="text-right px-2 py-1.5 font-mono tabular-nums">{r.esi > 0 ? r.esi : ""}</td>
                      <td className="text-right px-2 py-1.5 font-mono tabular-nums">{r.sal_adv > 0 ? fmt2(r.sal_adv) : ""}</td>
                      <td className="text-right px-2 py-1.5 font-mono tabular-nums font-bold text-base">{r.net_pay_round}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {modalRow && (
        <OverrideModal
          row={modalRow}
          monthStr={monthStr}
          maxDays={data.number_days || 31}
          onClose={() => setModalRow(null)}
          onSave={async (val, note) => {
            await saveOverride(modalRow.employee_id, val, note);
            setModalRow(null);
          }}
          onReset={async () => {
            await resetOverride(modalRow.employee_id);
            setModalRow(null);
          }}
        />
      )}
    </div>
  );
}

function OverrideModal({ row, monthStr, maxDays, onClose, onSave, onReset }) {
  const [value, setValue] = useState(row.no_of_holidays ?? 0);
  const [note, setNote] = useState(row.override?.note || "");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const v = parseInt(value, 10);
    if (Number.isNaN(v) || v < 0) {
      toast.error("Enter a whole number ≥ 0");
      return;
    }
    if (v > maxDays) {
      toast.error(`Cannot exceed calendar days in month (${maxDays})`);
      return;
    }
    setSaving(true);
    try {
      await onSave(v, note);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="payroll-override-modal">
      <form onSubmit={submit} className="bg-canvas border border-canvas-line max-w-lg w-full">
        <div className="border-b border-canvas-line px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Adjust Holidays / LOP Days · {monthStr}</div>
            <div className="text-lg font-semibold">{row.name} <span className="font-mono text-xs text-ink-muted">· {row.employee_code}</span></div>
            <div className="font-mono text-[10px] text-ink-muted">{row.branch_name} · Working {row.working_days_per_month}d · Auto: {row.computed_lop_days}</div>
          </div>
          <button type="button" onClick={onClose} data-testid="payroll-override-close" className="p-2 border border-canvas-line hover:border-ink"><X size={14} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 px-3 py-2">
            <Warning size={14} weight="fill" className="text-amber-600 mt-0.5" />
            <div>
              Override replaces the auto-computed LOP shortfall. Use for mid-month joiners/leavers, approved unpaid leave, or reconciliation. Enter <b>0</b> to waive LOP entirely for this month.
            </div>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Effective LOP Days (Holidays)</span>
            <input
              type="number"
              min={0}
              max={maxDays}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              data-testid="payroll-override-value"
              className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-base font-mono tabular-nums"
              autoFocus
            />
            <div className="mt-1 font-mono text-[10px] text-ink-muted">
              Auto-computed = {row.computed_lop_days} days. Days worked = {row.days_worked}/{row.working_days_per_month}. Max = {maxDays} (calendar days).
            </div>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Note (optional)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="payroll-override-note"
              rows={2}
              placeholder="e.g. Joined on 20th, prorated"
              className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
            />
          </label>
        </div>
        <div className="border-t border-canvas-line px-6 py-3 flex items-center justify-between">
          {row.override ? (
            <button type="button" onClick={onReset} data-testid="payroll-override-reset" className="px-3 py-2 text-xs font-mono uppercase tracking-wider border border-amber-500 text-amber-700 hover:bg-amber-50">
              Reset to auto ({row.computed_lop_days})
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-xs font-mono uppercase tracking-wider border border-canvas-line">Cancel</button>
            <button type="submit" disabled={saving} data-testid="payroll-override-save" className="px-4 py-2 bg-ink text-white text-xs font-semibold uppercase tracking-wider hover:bg-brand disabled:opacity-40">
              {saving ? "Saving…" : "Save Override"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
